/**
 * buildFilterSearchParams — extractSearchParams()의 반대 방향.
 * 파싱된(또는 그와 같은 모양의) 데이터 객체를 URLSearchParams로 직렬화한다.
 *
 * options 필드는 항상 "options[key]=value" 브래킷 표기로 쓴다 (extractSearchParams가
 * 읽는 포맷과 동일). 배열은 값마다 반복 append, 스칼라는 set. 빈 값(undefined/null/""/[])은
 * URL을 깨끗하게 유지하기 위해 생략한다.
 *
 * 브라우저 주소창에 필터 상태를 동기화할 때(usePageSearchState 등) 이 함수로 만든
 * URLSearchParams를 그대로 쓰면, 나중에 그 URL을 extractSearchParams로 다시 파싱했을 때
 * 항상 원래 값으로 왕복된다. (SSR에서 같은 URL을 그대로 파싱해도 동일한 결과)
 *
 * @example
 * const data = orderFilterSchema.parse({ keyword: "hi", options: { type: ["A", "B"] } });
 * const sp = buildFilterSearchParams(orderFilterSchema, data);
 * // sp.toString() === "keyword=hi&options%5Btype%5D=A&options%5Btype%5D=B"
 *
 * const restored = orderFilterSchema.parse(extractSearchParams(orderFilterSchema, sp));
 * // restored.options.type → ["A", "B"]  (왕복 성공)
 */
import { z } from "zod";
import { getOptionsShape } from "./zod-schema-utils";

function isEmpty(value: unknown): boolean {
    return (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
    );
}

export function buildFilterSearchParams(
    schema: z.ZodObject<z.ZodRawShape>,
    data: Record<string, unknown>
): URLSearchParams {
    const optionsShape = getOptionsShape(schema);
    const sp = new URLSearchParams();

    for (const [key, value] of Object.entries(data)) {
        if (key === "options" || isEmpty(value)) continue;
        sp.set(key, String(value));
    }

    const options = (data.options ?? {}) as Record<string, unknown>;
    for (const key of Object.keys(optionsShape)) {
        const value = options[key];
        if (isEmpty(value)) continue;

        const bracketKey = `options[${key}]`;
        if (Array.isArray(value)) {
            value.forEach((v) => sp.append(bracketKey, String(v)));
        } else {
            sp.set(bracketKey, String(value));
        }
    }

    return sp;
}
